import {getStore} from "@/composables/get.store";
import {injectStrict} from "@/composables/provideInject";
import type {
  CopyEvent,
  CustomQuestionUpdateEvent,
  ResponderMatch,
  ResponderMatchGroup,
  SpreadsheetFileReader,
  SynonymEvent,
} from "@/composables/responder/types";
import {getToast} from "@/composables/toast";
import type {PGStore} from "@/store/index-types";
import type {Ref} from "vue";
import {provide} from "vue";
import axios from "axios";
import Papa from "papaparse";
import type {PaginationResponse} from "pg-isomorphic/api";
import type {DataShareLinkInput, DataShareLinkResponse, TabLite, TopicLite} from "pg-isomorphic/api/datashare";
import type {APIEntityInfo} from "pg-isomorphic/api/entity";
import type {
  AssessmentInfoLite,
  AssessmentsLite,
  BatchTagPayload,
  ConfirmResponderEmailRequest,
  CreateResponderUserRequest,
  CustomResponderQuestion,
  GetBestMatchesResult,
  MatchGroups,
  QuestionManagementSearchRequest,
  ResponderAccountEventEntry,
  ResponderAccountEventReportRequest,
  ResponderCopyEventData,
  ResponderCopyEventEntry,
  ResponderKeywordSearchType,
  ResponderPlatform,
  ResponderQuestionData,
  ResponderQuestionExportData,
  ResponderQuestionHistoryData,
  ResponderQuestionUploadRequest,
  ResponderQuestionUploadResponse,
  ResponderRelevanceEventEntry,
  ResponderReportRequest,
  ResponderSearchEventEntry,
  ResponderSearchVersion,
  ResponderSynonymData,
  ResponderTagLite,
  SimpleResponse,
} from "pg-isomorphic/api/responder";
import type {GraphiteQuestionSuggestionInput} from "pg-isomorphic/api/user";
import {QuestionClass} from "pg-isomorphic/enums/question";
import type {JSONObject, JSONQuestion} from "pg-isomorphic/utils";
import * as XLSX from "xlsx";
import type {WorkBook, WorkSheet} from "xlsx";
import type {ConnectionProfileData} from "@/composables/connections/types";

function createFileReader(): SpreadsheetFileReader {
  const fileReader: SpreadsheetFileReader = new FileReader() as SpreadsheetFileReader;

  fileReader.promise = new Promise((resolve, reject) => {
    fileReader.onload = () => resolve();
    fileReader.onabort = () => resolve();
    fileReader.onerror = () => reject();
  });

  return fileReader;
}

async function parseCsv(input: string): Promise<ResponderQuestionUploadRequest[]> {
  return await new Promise<ResponderQuestionUploadRequest[]>((resolve, reject) => {
    // trim any new lines from the end (but not the start, need to preserve line count)
    const {data: rows, errors} = Papa.parse<string[]>(input.trimEnd(), {
      skipEmptyLines: false, // stripped later (to preserve line numbers)
      transform: (val) => val.trim(), // trim each value individually
    });

    if (errors.length) {
      reject(errors);
      return;
    }

    const result = rows
      .map<ResponderQuestionUploadRequest>(([label, answer, ...tags], lineNum) => {
        // strip headers
        if (["label", "question"].includes(label?.toLowerCase()) && answer?.toLowerCase() === "answer") {
          return null;
        }

        // strip lines with no entries
        if ((label || "").trim() === "" && (answer || "").trim() === "") {
          return null;
        }

        const tagLabels = tags.flatMap((tagStr) => (tagStr || "").split(",").map((t) => t.trim())).filter((t) => !!t);

        return {label, answer, lineNum: lineNum + 1, tagLabels};
      })
      .filter((row) => !!row);

    resolve(result);
  });
}

async function readAsBinaryString(file: File): Promise<string> {
  const fileReader: SpreadsheetFileReader = createFileReader();

  fileReader.readAsBinaryString(file);

  await fileReader.promise;

  return fileReader.result as string;
}

async function fileToText(file: File): Promise<string> {
  const book: WorkBook = XLSX.read(await readAsBinaryString(file), {
    type: "binary",
  });

  const sheet: WorkSheet = book.Sheets[book.SheetNames[0]];

  return XLSX.utils.sheet_to_csv(sheet);
}

export function isCustomResponderQuestion(
  question: JSONQuestion | ResponderQuestionData,
): question is ResponderQuestionData {
  return "answer" in question;
}

export async function getResponderMatchData(
  userQuestions: string[],
  numMatches = 5,
  searchVersion: ResponderSearchVersion,
  keywordSearchType: ResponderKeywordSearchType,
  platform: ResponderPlatform,
): Promise<GetBestMatchesResult> {
  return (
    await getStore<PGStore>().httpGet<GetBestMatchesResult>("/api/responder/matches", {
      inputs: userQuestions,
      numMatches,
      searchVersion,
      keywordSearchType,
      platform,
    })
  ).data;
}

export async function batchCreateResponderQuestions(
  questions: ResponderQuestionUploadRequest[],
): Promise<ResponderQuestionUploadResponse> {
  return (
    await getStore<PGStore>().httpPost<ResponderQuestionUploadResponse>("/api/responder/questions-batch", {questions})
  ).data;
}

export async function getResponderQuestionHistory(questionId: string): Promise<ResponderQuestionHistoryData[]> {
  return (
    await getStore<PGStore>().httpGet<{history: ResponderQuestionHistoryData[]}>(`/api/responder/history/${questionId}`)
  ).data.history;
}

export interface TagLikes {
  tags: ResponderTagLite[];
  topics: TopicLite[];
  assessments: AssessmentInfoLite[];
}

export const emptyTagLikes: TagLikes = {
  tags: [],
  topics: [],
  assessments: [],
};

export async function uploadQuestionsFromFile(
  file: File,
  tagLikes: TagLikes,
): Promise<ResponderQuestionUploadResponse> {
  let contents: string;

  // TODO how big will these files be?
  if (file.name.endsWith(".csv") || file.name.endsWith(".tsv")) {
    contents = await file.text();
  } else if (file.name.endsWith(".xls") || file.name.endsWith(".xlsx")) {
    contents = await fileToText(file);
  } else {
    throw new Error("Unacceptable file format.");
  }

  const questions: ResponderQuestionUploadRequest[] = (await parseCsv(contents)).map((q) => ({
    ...q,
    tags: (tagLikes.tags || []).map((t) => t.id),
    topics: (tagLikes.topics || []).map((t) => t.id),
    assessments: (tagLikes.assessments || []).map((a) => a.id),
  }));

  return batchCreateResponderQuestions(questions);
}

export async function createResponderQuestion(
  question: ResponderQuestionData | CustomResponderQuestion,
): Promise<CustomResponderQuestion> {
  return (await getStore<PGStore>().httpPost<CustomResponderQuestion>("/api/responder/questions", question)).data;
}

export async function updateResponderQuestion(
  id: string,
  question: ResponderQuestionData | CustomResponderQuestion,
): Promise<CustomResponderQuestion> {
  return (await getStore<PGStore>().httpPatch<CustomResponderQuestion>(`/api/responder/question/${id}`, question)).data;
}

export async function batchAddTags(payload: BatchTagPayload): Promise<CustomResponderQuestion[]> {
  return (await getStore<PGStore>().httpPost<CustomResponderQuestion[]>(`/api/responder/batch-tag-questions`, payload))
    .data;
}

export async function createResponderSynonym(
  question: JSONQuestion | ResponderQuestionData,
  userQuestionText: string,
  displayedAncestors: string[],
): Promise<ResponderSynonymData> {
  const synonyms: ResponderSynonymData[] = [
    {
      question: question._id,
      label: userQuestionText,
      questionClass: isCustomResponderQuestion(question) ? QuestionClass.RESPONDER : QuestionClass.PROFILE,
      displayedAncestors,
    },
  ];

  return (await getStore<PGStore>().httpPost<{synonyms: ResponderSynonymData[]}>("/api/responder/synonyms", {synonyms}))
    .data.synonyms[0];
}

export async function deleteResponderSynonym(synonymId: string): Promise<ResponderSynonymData> {
  return (await getStore<PGStore>().httpDelete<ResponderSynonymData>(`/api/responder/synonym/${synonymId}`)).data;
}

export async function deleteResponderQuestion(responderQuestionId: string): Promise<ResponderQuestionData> {
  return (await getStore<PGStore>().httpDelete(`/api/responder/question/${responderQuestionId}`)).data;
}

export async function deleteAllResponderQuestions(): Promise<ResponderQuestionData> {
  return (await getStore<PGStore>().httpDelete(`/api/responder/all-questions`)).data;
}

export async function buryResponderQuestion(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _question: JSONQuestion | ResponderQuestionData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _userQuestionText: string,
): Promise<void> {
  // tslint:disable-next-line:no-console
  console.error("NYI");
}

export async function createResponderCopyEvent(
  question: JSONQuestion | ResponderQuestionData,
  userQuestionText: string,
  displayedAncestors: string[],
): Promise<ResponderCopyEventData> {
  const copyEvents: ResponderCopyEventData[] = [
    {
      question: question._id,
      label: userQuestionText,
      questionClass: isCustomResponderQuestion(question) ? QuestionClass.RESPONDER : QuestionClass.PROFILE,
      displayedAncestors,
    },
  ];

  return (
    await getStore<PGStore>().httpPost<{copyEvents: ResponderCopyEventData[]}>("/api/responder/copy-events", {
      copyEvents,
    })
  ).data.copyEvents[0];
}

export function collectSynonymsFromMatchGroups(matchGroups: ResponderMatchGroup[]): ResponderSynonymData[] {
  const synonyms: ResponderSynonymData[] = [];

  matchGroups.forEach((matchGroup) => {
    matchGroup.matches.forEach((match) => {
      if (match.synonym) {
        synonyms.push(match.synonym);
      }
    });
  });

  return synonyms;
}

export async function getResponderDataShareLink(payload: DataShareLinkInput): Promise<string> {
  return (await getStore<PGStore>().httpPost<DataShareLinkResponse>("/api/data-share/data-link", payload)).data
    .shareDataLink;
}

export async function suggestGraphiteQuestion(payload: GraphiteQuestionSuggestionInput): Promise<void> {
  await getStore<PGStore>().httpPost<void>("/api/responder/suggest-graphite-question", payload);
}

export async function fetchKeywordSearchResults(
  keywords: string[],
  input: string,
  numMatches: number,
  searchVersion: ResponderSearchVersion,
  keywordSearchType: ResponderKeywordSearchType,
  platform: ResponderPlatform,
): Promise<MatchGroups> {
  return (
    await getStore<PGStore>().httpGet<MatchGroups>("/api/responder/keyword-search", {
      keywords,
      input,
      numMatches,
      searchVersion,
      keywordSearchType,
      platform,
    })
  ).data;
}

export async function responderSignUp(payload: CreateResponderUserRequest): Promise<SimpleResponse> {
  return (await getStore<PGStore>().httpPost<SimpleResponse>("/api/responder/sign-up", payload)).data;
}

export async function responderConfirmEmail(payload: ConfirmResponderEmailRequest): Promise<SimpleResponse> {
  return (await getStore<PGStore>().httpPost<SimpleResponse>("/api/responder/confirm-email", payload)).data;
}

export async function doesUserNeedToUploadQuestions(): Promise<boolean> {
  return !!(await getStore<PGStore>().httpGet<{needsQuestions: boolean}>("/api/responder/needs-questions")).data
    ?.needsQuestions;
}

export async function getTopics(): Promise<TabLite[]> {
  return (await getStore<PGStore>().httpGet<TabLite[]>("/api/responder/topics")).data;
}

export async function getAssessments(): Promise<AssessmentsLite> {
  return (await getStore<PGStore>().httpGet<AssessmentsLite>("/api/responder/assessments")).data;
}

export async function getResponderQuestionTags(): Promise<ResponderTagLite[]> {
  return (await getStore<PGStore>().httpGet<ResponderTagLite[]>("/api/responder/tags")).data;
}

export async function createResponderQuestionTag(tag): Promise<ResponderTagLite> {
  try {
    return (await getStore<PGStore>().httpPost<ResponderTagLite>("/api/responder/tags", tag)).data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      getToast().error("Duplicate tag. Choose another.");
    }
    throw err;
  }
}

export async function updateResponderQuestionTag(id, tag): Promise<ResponderTagLite> {
  return (await getStore<PGStore>().httpPut<ResponderTagLite>(`/api/responder/tags/${id}`, tag)).data;
}

export async function deleteResponderQuestionTag(id): Promise<void> {
  await getStore<PGStore>().httpDelete<void>(`/api/responder/tags/${id}`);
}

export async function transferQuestions(fromEntityIds: string[], toEntityId: string): Promise<void> {
  await getStore<PGStore>().httpPost<SimpleResponse>("/api/responder/transfer", {fromEntityIds, toEntityId});
}

// Question Admin
export async function searchCustomQuestions(
  options: QuestionManagementSearchRequest,
): Promise<PaginationResponse<CustomResponderQuestion[]>> {
  return (
    await getStore<PGStore>().httpGet<PaginationResponse<CustomResponderQuestion[]>>(
      "/api/responder/questions",
      options,
    )
  ).data;
}

export async function getAllQuestionsForExport(): Promise<ResponderQuestionExportData[]> {
  return (await getStore<PGStore>().httpGet<ResponderQuestionExportData[]>("/api/responder/export-question-data")).data;
}

const baseSearchEventsReportUrl = "/api/responder/search-events-report";
const baseCopyEventsReportUrl = "/api/responder/copy-events-report";
const baseRelevanceEventsReportUrl = "/api/responder/relevance-events-report";
const baseAccountEventsReportUrl = "/api/responder/account-events-report";

export async function getSearchEvents(
  requestParams: ResponderReportRequest,
): Promise<PaginationResponse<ResponderSearchEventEntry[]>> {
  return (
    await getStore<PGStore>().httpGet<PaginationResponse<ResponderSearchEventEntry[]>>(
      baseSearchEventsReportUrl,
      requestParams,
    )
  ).data;
}

export async function getCopyEvents(
  requestParams: ResponderReportRequest,
): Promise<PaginationResponse<ResponderCopyEventEntry[]>> {
  return (
    await getStore<PGStore>().httpGet<PaginationResponse<ResponderCopyEventEntry[]>>(
      baseCopyEventsReportUrl,
      requestParams,
    )
  ).data;
}

export async function getRelevanceEvents(
  requestParams: ResponderReportRequest,
): Promise<PaginationResponse<ResponderRelevanceEventEntry[]>> {
  return (
    await getStore<PGStore>().httpGet<PaginationResponse<ResponderRelevanceEventEntry[]>>(
      baseRelevanceEventsReportUrl,
      requestParams,
    )
  ).data;
}

export async function getAccountEvents(
  requestParams: ResponderAccountEventReportRequest,
): Promise<PaginationResponse<ResponderAccountEventEntry[]>> {
  return (
    await getStore<PGStore>().httpGet<PaginationResponse<ResponderAccountEventEntry[]>>(
      baseAccountEventsReportUrl,
      requestParams,
    )
  ).data;
}

export async function getEntitiesThatHaveUsedResponder(): Promise<Array<APIEntityInfo>> {
  return (await getStore<PGStore>().httpGet<APIEntityInfo[]>("/api/responder/entities-using-responder")).data;
}

export async function responderNeedsIndexingForActiveEntity(): Promise<boolean> {
  return (await getStore<PGStore>().httpGet<{needsIndexing: boolean}>(`/api/responder/needs-indexing`)).data
    .needsIndexing;
}

export async function indexResponderQuestionsForActiveEntity(): Promise<void> {
  await getStore<PGStore>().httpPost(`/api/responder/index`, {});
}

function strUnlessUndefined(strish: string | number | boolean | undefined | null): string {
  return strish === undefined ? "" : "" + strish;
}

function createQueryString(
  data: Record<string, string | number | boolean | string[] | number[] | undefined | null>,
): string {
  return Object.entries(data)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return (value as any).map((val) => `${key}[]=${strUnlessUndefined(val)}`).join("&");
      }

      return `${key}=${strUnlessUndefined(value)}`;
    })
    .join("&");
}

export function exportSearchEvents(requestParams: ResponderReportRequest): void {
  const queryParams = createQueryString({...requestParams});
  window.open(`${baseSearchEventsReportUrl}?${queryParams}`);
}

export function exportCopyEvents(requestParams: ResponderReportRequest): void {
  const queryParams = createQueryString({...requestParams});
  window.open(`${baseCopyEventsReportUrl}?${queryParams}`);
}

export function exportRelevanceEvents(requestParams: ResponderReportRequest): void {
  const queryParams = createQueryString({...requestParams});
  window.open(`${baseRelevanceEventsReportUrl}?${queryParams}`);
}

export function exportAccountEvents(requestParams: ResponderAccountEventReportRequest): void {
  const queryParams = createQueryString({...requestParams});
  window.open(`${baseAccountEventsReportUrl}?${queryParams}`);
}

export enum ResponderProviderKey {
  Copy = "Copy",
  DeleteQuestion = "DeleteQuestion",
  AnswerInGraphiteConnect = "AnswerInGraphiteConnect",
  SaveCustomQuestion = "SaveCustomQuestion",
  UnmarkAsRelevant = "UnmarkAsRelevant",
  MarkAsRelevant = "MarkAsRelevant",
  MarkAsIrrelevant = "MarkAsIrrelevant",
  ShowHistory = "ShowHistory",

  ShowScores = "ShowScores",
  ShowTokens = "ShowTokens",
  ShowQuestionType = "ShowQuestionType",
  Answers = "Answers",
  UserQuestion = "UserQuestion",
  Synonyms = "Synonyms",
  ConnectionData = "ConnectionData",
}

export interface ResponderProviderMap {
  [ResponderProviderKey.Copy]: (event: CopyEvent) => void;
  [ResponderProviderKey.DeleteQuestion]: (customQuestion: ResponderQuestionData) => Promise<void>;
  [ResponderProviderKey.AnswerInGraphiteConnect]: (question: JSONQuestion) => void;
  [ResponderProviderKey.SaveCustomQuestion]: (event: CustomQuestionUpdateEvent) => void;
  [ResponderProviderKey.UnmarkAsRelevant]: (synonymId: string) => void;
  [ResponderProviderKey.MarkAsRelevant]: (event: SynonymEvent) => void;
  [ResponderProviderKey.MarkAsIrrelevant]: (event: SynonymEvent) => void;
  [ResponderProviderKey.ShowHistory]: (question: JSONQuestion | ResponderQuestionData) => void;
  [ResponderProviderKey.ShowTokens]: Ref<boolean>;
  [ResponderProviderKey.ShowQuestionType]: Ref<boolean>;
  [ResponderProviderKey.ShowScores]: Ref<boolean>;
  [ResponderProviderKey.Answers]: Ref<JSONObject>;
  [ResponderProviderKey.UserQuestion]: Ref<string>;
  [ResponderProviderKey.Synonyms]: Ref<ResponderSynonymData[]>;
  [ResponderProviderKey.ConnectionData]: Ref<ConnectionProfileData>;
}

export function responderInject<K extends ResponderProviderKey>(
  key: K,
  fallback?: ResponderProviderMap[K],
): ResponderProviderMap[K] {
  return injectStrict<ResponderProviderMap[K]>(key as any, fallback);
}

export function responderProvide<K extends ResponderProviderKey>(key: K, data: ResponderProviderMap[K]): void {
  // @ts-ignore -- Cameron will need to fix this type error
  provide(key, data);
}

export function makeUniqueKey(match: ResponderMatch) {
  return match.customQuestion ? match.customQuestion._id : match.graphiteQuestion._id + match.graphiteQuestion.key;
}
